/**
 * The last answer to each read, kept on the device so the installed app opens
 * with no connection on the last state it showed (roadmap item app-installable).
 *
 * The page keeps them itself, from `apiFetch`, and not the service worker, so
 * the worker never stands between the page and the platform: every request
 * to the platform goes out from the page as it always did, with the session
 * cookie and the CSRF token, and every cookie the platform sets lands where
 * the page reads it. The worker only serves the app's own files (`src/sw/sw.ts`).
 *
 * What is never kept or served:
 * - one account's reads to another: every copy belongs to the account
 *   `noteAccount` last named (`AuthContext`, from `/api/auth/me`); a
 *   different one, a sign-in, a sign-up and a sign-out forget them all;
 * - a write: only a GET is kept, and a write that finds no connection fails
 *   (`OfflineError`) and is not kept to send later.
 *
 * Everything here fails quietly: a browser with no cache storage (an old one,
 * a private window that refuses it, the test runner) simply keeps nothing.
 */

/** The cache the copies live in. */
export const DATA = 'ops-data';
/** The entry naming whose copies `DATA` holds. */
export const ACCOUNT_KEY = '/__ops/account';
/** At most this many copies; the oldest go first. */
export const MAX_COPIES = 300;
/** A larger answer is not kept (a terrain's heightmap is no page's last state). */
export const MAX_COPY_CHARS = 1_500_000;

const STORED_AT = 'X-Ops-Stored-At';

function storage(): CacheStorage | null {
  try {
    return typeof caches !== 'undefined' ? caches : null;
  } catch {
    return null;
  }
}

/** Whether this browser can keep copies at all. */
export function canKeep(): boolean {
  return storage() !== null;
}

function keyOf(path: string): string {
  return new URL(path, window.location.origin).href;
}

/** Who `/api/auth/me` names: an account's id, or nobody. */
export function accountOf(me: unknown): string {
  if (me && typeof me === 'object') {
    const m = me as { authenticated?: unknown; id?: unknown };
    if (m.authenticated === true && m.id != null) return `account:${String(m.id)}`;
  }
  return 'anonymous';
}

/** Forget every copy, whoever it belonged to. */
export async function forgetAll(): Promise<void> {
  try {
    await storage()?.delete(DATA);
  } catch {
    // Nothing was kept.
  }
}

/** Where this browser remembers that its account signed out here (`signedOutHere`). */
const SIGNED_OUT_KEY = 'ops:signed-out';

/**
 * Whether the last account on this browser signed out here, so a page with no
 * connection can say why the device has no copy of it: sign-out forgets them
 * all (web card 227, 2026-10-19). Only a flag, no account in it; a browser
 * that refuses storage just says the plainer sentence.
 */
export function signedOutHere(): boolean {
  try {
    return localStorage.getItem(SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

/** Sign-out sets it; a sign-in, a sign-up or an account `/api/auth/me` names clears it. */
export function noteSignedOut(out: boolean): void {
  try {
    if (out) localStorage.setItem(SIGNED_OUT_KEY, '1');
    else localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    // Nothing to remember it in.
  }
}

/**
 * `/api/auth/me` named an account: when it is not the one the copies belong
 * to, they are all forgotten before anything else is kept.
 */
export async function noteAccount(who: string): Promise<void> {
  const store = storage();
  if (!store) return;
  try {
    const data = await store.open(DATA);
    const held = await data.match(keyOf(ACCOUNT_KEY));
    if (held && (await held.text()) === who) return;
    await store.delete(DATA);
    await (await store.open(DATA)).put(keyOf(ACCOUNT_KEY), new Response(who));
  } catch {
    // Keep nothing rather than keep it for the wrong account.
  }
}

/** Keep the answer to a read, for whoever the copies belong to; none before an account is named. */
export async function keepCopy(path: string, text: string, now = Date.now()): Promise<void> {
  const store = storage();
  if (!store || text.length > MAX_COPY_CHARS) return;
  try {
    const data = await store.open(DATA);
    if (!(await data.match(keyOf(ACCOUNT_KEY)))) return;
    await data.put(
      keyOf(path),
      new Response(text, {
        headers: { 'Content-Type': 'application/json', [STORED_AT]: String(now) },
      }),
    );
    const keys = await data.keys();
    const copies = keys.filter((k) => new URL(k.url).pathname !== ACCOUNT_KEY);
    for (const old of copies.slice(0, Math.max(0, copies.length - MAX_COPIES))) {
      await data.delete(old);
    }
  } catch {
    // A full or refused store keeps nothing more; the page still has its answer.
  }
}

/** The kept answer to a read, and when it was kept. */
export async function readCopy(path: string): Promise<{ text: string; storedAt: number } | null> {
  const store = storage();
  if (!store) return null;
  try {
    const hit = await (await store.open(DATA)).match(keyOf(path));
    if (!hit) return null;
    return { text: await hit.text(), storedAt: Number(hit.headers.get(STORED_AT)) || 0 };
  } catch {
    return null;
  }
}
